import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { ChevronRight, Workflow, Zap } from "lucide-react";
import type { CockpitButton, FieldDef, FormButtonElement, RecordData, WorkflowGraph, WorkflowTransition } from "../core";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { enumKey } from "./cell_utils";
import { HeaderButton } from "./FormHeader";
import { evalRowInvisible } from "../widgets/rowVisibility";

// CockpitStatus — the cockpit <status> card (form-view revamp Enh 14, faithful
// to the frozen mockup's STATUS sidecard). Brings the header statusbar + its
// transition buttons INTO the cockpit:
//   current-stage chip + health (In Progress / Complete · Age) · a horizontal
//   lifecycle dot-rail · the visited-path caption · the available transition
//   buttons (run through onRunTransition so guards/approval/hooks fire) · a
//   "View full lifecycle" link that opens the workflow drawer.
// No workflow on the field → degrades to the plain current-value chip.

type Rec = Record<string, unknown> | null | undefined;

interface Props {
    field: string | null;
    showAge: boolean;
    buttons: CockpitButton[];
    /** Enum-rail subset/order mirrored from the header statusbar_visible. */
    statusbarVisible?: string[] | null;
    record: Rec;
    modelKey?: string;
    recordRef?: string | null;
    fieldDefs?: FieldDef[];
    actionSlug?: string;
    /** Action buttons declared in <header>, surfaced here (Enh 14). */
    headerButtons?: FormButtonElement[];
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
}

function formatValue(value: unknown): string {
    if (value == null || value === "") return "";
    if (typeof value === "object") {
        const o = value as Record<string, unknown>;
        return typeof o.display_name === "string" ? o.display_name : typeof o.label === "string" ? o.label : "";
    }
    return String(value);
}

// Resolve a plain-enum field's active value to its human label (the "normal
// status bar" case) via the field's selection options; falls back to the value.
function enumLabel(fieldDefs: FieldDef[] | undefined, field: string | null, value: unknown): string {
    const raw = formatValue(value);
    if (!field || !fieldDefs) return raw;
    const fd = fieldDefs.find((f) => f.name === field);
    const opt = fd?.selection?.find(([k]) => k === String(enumKey(value)));
    return opt ? opt[1] : raw;
}

function ageDays(record: Rec): number | null {
    const created = record?.created_at_utc;
    if (typeof created !== "string") return null;
    const then = Date.parse(created);
    if (Number.isNaN(then)) return null;
    return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

export function CockpitStatus({
    field,
    showAge,
    buttons,
    statusbarVisible,
    record,
    modelKey,
    recordRef,
    fieldDefs,
    actionSlug,
    headerButtons,
    onRunButton,
    onRunTransition,
}: Props): ReactNode {
    const runtime = useRuntime();
    const [graph, setGraph] = useState<WorkflowGraph | null>(null);
    const [availTransitions, setAvailTransitions] = useState<WorkflowTransition[]>([]);
    const [busy, setBusy] = useState(false);

    const currentRaw = field ? record?.[field] : null;
    useEffect(() => {
        if (!modelKey || !recordRef) return;
        let alive = true;
        void (async () => {
            try {
                // Graph drives the stepper; `available()` (the same source the
                // header statusbar uses) drives the transition buttons — the
                // graph's `available` flag is instance-derived and unreliable.
                const [g, avail] = await Promise.all([
                    runtime.services.workflow.graph(modelKey, recordRef),
                    runtime.services.workflow.available(modelKey, recordRef).catch(() => []),
                ]);
                if (!alive) return;
                setGraph(g && g.definition ? g : null);
                setAvailTransitions(avail ?? []);
            } catch {
                if (alive) {
                    setGraph(null);
                    setAvailTransitions([]);
                }
            }
        })();
        return () => {
            alive = false;
        };
    }, [runtime, modelKey, recordRef, currentRaw]);

    const stages = graph ? [...graph.stages].sort((a, b) => a.sequence - b.sequence) : [];
    // Resolve the current stage by matching the record's field value to the
    // stage CODE (the statusbar's contract) — `is_current`/`current_stage_id`
    // are instance-derived and unset until a record has a workflow instance,
    // so relying on them shows the raw enum code (e.g. QUOTE_DRAFT) for new rows.
    const currentCode = field ? String(enumKey(record?.[field]) ?? "") : "";
    const currentStage = stages.find((s) => s.code === currentCode) || stages.find((s) => s.is_current) || null;
    // Transition buttons come from the live `available()` list (guard/RBAC
    // filtered for the current principal) — identical to the header statusbar.
    //
    // De-dup against header / DSL verbs: a `<header>` <button> (or <status>
    // button) that wraps a workflow transition (`transition_by_command`)
    // duplicates the auto-rendered transition (e.g. crm.opportunity's
    // "Start Negotiation" / "Close Won" buttons). Drop the auto transition
    // when a SAME-LABEL, currently-VISIBLE verb exists — the authored button
    // is richer (icon / confirm / payload / invisible predicate) and fires the
    // same transition. Matching only visible buttons means a guard-HIDDEN
    // transition whose button is also hidden isn't wrongly suppressed, and a
    // guard-hidden transition with a still-visible button (the entry point)
    // simply has no transition to drop. Views with no verbs (e.g. crm.quote)
    // keep every transition.
    const rec = (record as RecordData | null) ?? null;
    const verbLabels = new Set<string>(
        [
            ...(headerButtons ?? [])
                .filter((b) => !(rec && evalRowInvisible(b.invisible ?? null, rec)))
                .map((b) => (b.labelField && rec ? String(rec[b.labelField] ?? "") : "") || b.label || b.name),
            ...buttons.map((b) => b.label ?? b.command ?? ""),
        ]
            .map((s) => String(s ?? "").trim().toLowerCase())
            .filter(Boolean),
    );
    const transitions = availTransitions.filter(
        (t) => !verbLabels.has(String(t.name ?? "").trim().toLowerCase()),
    );
    // Terminal = the stage has no outbound transitions defined at all (distinct
    // from "none currently available" due to guards).
    const terminal = Boolean(
        graph && currentStage && !graph.transitions.some((t) => t.from_stage_id === currentStage.id),
    );

    // Active value of the status field: workflow stage label, else the enum
    // option label (the "normal status bar"), else the raw value.
    const chipLabel = currentStage?.label || enumLabel(fieldDefs, field, field ? record?.[field] : undefined);
    const age = showAge ? ageDays(record) : null;
    const health = terminal ? "Complete" : "In Progress";

    // Rail = the happy path only. Off-path terminal branches (Rejected /
    // Cancelled / Expired / Lost — stages with NO outbound transition) are
    // dropped from the rail unless the record is actually sitting on one, so
    // the rail stays a clean linear flow like the mockup instead of fanning out.
    const hasOutgoing = (s: { id: string }) =>
        graph?.transitions.some((t) => t.from_stage_id === s.id) ?? false;
    // Terminal stages have no outbound transition. The happy-path END (e.g.
    // Accepted) is terminal too, so keep the FIRST terminal by sequence (the
    // success end) and drop the later off-path branches (Rejected / Cancelled /
    // Expired / Lost) unless the record is actually sitting on one.
    const terminals = stages.filter((s) => !hasOutgoing(s));
    const successEndId = terminals.length
        ? terminals.reduce((a, b) => (a.sequence <= b.sequence ? a : b)).id
        : null;
    const railStages = stages.filter(
        (s) => hasOutgoing(s) || s.id === successEndId || s.is_visited || s.id === currentStage?.id,
    );
    const railCurrentIdx = currentStage ? railStages.findIndex((s) => s.id === currentStage.id) : -1;

    // Unified lifecycle rail. Workflow graph wins; otherwise — for a plain-enum
    // statusbar (the "normal status bar", e.g. crm.lead.status) — fall back to
    // the field's own selection options so the cockpit still shows the stages.
    const enumFd = field ? fieldDefs?.find((f) => f.name === field) : undefined;
    const allEnumOptions = !graph && enumFd?.selection ? enumFd.selection : [];
    // Honor the header statusbar_visible subset/order when present; always keep
    // the current value visible even if it falls outside the declared subset.
    const enumOptions = ((): [string, string][] => {
        if (!allEnumOptions.length || !statusbarVisible?.length) return allEnumOptions;
        const byKey = new Map(allEnumOptions);
        const ordered = statusbarVisible.filter((k) => byKey.has(k)).map((k) => [k, byKey.get(k)!] as [string, string]);
        if (currentCode && byKey.has(currentCode) && !ordered.some(([k]) => k === currentCode)) {
            ordered.push([currentCode, byKey.get(currentCode)!]);
        }
        return ordered;
    })();
    const rail: { id: string; label: string }[] = railStages.length
        ? railStages.map((s) => ({ id: s.id, label: s.label }))
        : enumOptions.map(([code, label]) => ({ id: code, label }));
    const railIdx = railStages.length
        ? railCurrentIdx
        : enumOptions.findIndex(([code]) => code === currentCode);

    const runTransition = async (code: string, name: string) => {
        if (!onRunTransition || busy) return;
        setBusy(true);
        try {
            await onRunTransition(code, name);
        } finally {
            setBusy(false);
        }
    };

    const runButton = (b: CockpitButton) => {
        if (!onRunButton || !b.command) return;
        onRunButton({
            type: "button",
            name: b.command,
            command: b.command,
            label: b.label ?? null,
            icon: b.icon ?? null,
            confirm: b.confirm ?? null,
        } as FormButtonElement);
    };

    // The card renders whenever there's something to show: a status value, a
    // lifecycle rail, OR actionable verbs (header buttons / transitions / DSL
    // verbs). A header with ONLY buttons (no status field) still surfaces here
    // as an "Actions" card — nothing actionable is left stranded in the header.
    const hasActions = (headerButtons?.length ?? 0) > 0 || transitions.length > 0 || buttons.length > 0;
    const hasStatus = Boolean(chipLabel) || rail.length > 0;
    if (!hasStatus && !hasActions) return null;
    const cardTitle = hasStatus ? "Status" : "Actions";
    const HeadIcon = hasStatus ? Workflow : Zap;

    return (
        <div className="cockpit-card cockpit-card--status">
            <div className="cockpit-card__head">
                <HeadIcon size={13} />
                {cardTitle}
            </div>
            <div className="cockpit-card__body">
                <div className="cockpit-statusrow">
                    {chipLabel ? (
                        <span className="cockpit-statuschip">
                            <span className="cockpit-statuschip__dot" />
                            {chipLabel}
                        </span>
                    ) : null}
                    {graph ? (
                        <span className="cockpit-statusrow__sub">
                            <span className={`cockpit-blinkdot${terminal ? " cockpit-blinkdot--done" : ""}`} />
                            {health}
                            {age != null ? ` · Age ${age}d` : ""}
                        </span>
                    ) : null}
                </div>

                {/* horizontal lifecycle dot-rail (workflow happy path, or the
                    enum field's selection options for a plain status bar) */}
                {rail.length > 0 ? (
                    <div
                        className="cockpit-ministeps"
                        style={{
                            // Fill the track to the current dot, plus HALF the
                            // outgoing segment so the current stage reads as
                            // "in progress"; the success-end stage fills fully.
                            // Dots are centred on a full-width track (a half
                            // segment of line sits before the first dot and after
                            // the last). Fill runs to half-past the current dot:
                            // (idx + 1) / n covers prior segments + the current's
                            // outgoing half; the last stage fills fully.
                            ["--steps-fill" as string]: (() => {
                                const n = rail.length;
                                if (n < 1 || railIdx < 0) return "0";
                                return String(Math.min(1, (railIdx + 1) / n));
                            })(),
                        }}
                    >
                        <span className="cockpit-ministeps__track" aria-hidden="true" />
                        <span className="cockpit-ministeps__fill" aria-hidden="true" />
                        {rail.map((s, i) => {
                            const isCurrent = i === railIdx;
                            const done = railIdx >= 0 && i < railIdx;
                            const stepCls = [
                                "cockpit-mstep",
                                isCurrent ? "cockpit-mstep--current" : "",
                                done ? "cockpit-mstep--done" : "",
                            ].filter(Boolean).join(" ");
                            return (
                                <div key={s.id} className={stepCls} title={s.label}>
                                    <span className="cockpit-mstep__dot" />
                                    <span className="cockpit-mstep__label">{s.label}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                {/* transition buttons + header action verbs + DSL <status> verbs */}
                {transitions.length > 0 || (headerButtons?.length ?? 0) > 0 || buttons.length > 0 ? (
                    <div className="cockpit-sideactions">
                        {(headerButtons ?? []).map((el) => (
                            <HeaderButton
                                key={`h-${el.name}`}
                                el={el}
                                record={(record as RecordData | null) ?? null}
                                actionSlug={actionSlug ?? ""}
                                recordRef={recordRef ?? null}
                                onClick={onRunButton}
                                className="cockpit-sidebtn"
                            />
                        ))}
                        {transitions.map((t: WorkflowTransition, i) => (
                            <button
                                key={t.code}
                                type="button"
                                className={`cockpit-sidebtn${i === 0 ? " cockpit-sidebtn--primary" : ""}`}
                                onClick={() => void runTransition(t.code, t.name)}
                                disabled={busy}
                            >
                                {t.name}
                                {t.requires_approval ? " →" : ""}
                            </button>
                        ))}
                        {buttons.map((b, i) => (
                            <button
                                key={`b-${b.command ?? b.label ?? i}`}
                                type="button"
                                className="cockpit-sidebtn"
                                onClick={() => runButton(b)}
                            >
                                {b.label ?? b.command ?? "Action"}
                            </button>
                        ))}
                    </div>
                ) : null}

                {/* open the full workflow drawer (owned by RecordStrip) */}
                {graph ? (
                    <button
                        type="button"
                        className="cockpit-sidelink"
                        onClick={() => runtime.eventBus.emit("cockpit.open_workflow", {})}
                    >
                        View full lifecycle
                        <ChevronRight size={13} />
                    </button>
                ) : null}
            </div>
        </div>
    );
}
